import { supabase } from '@/core/database/supabase';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { PermissionRole } from '../types/permissionsList.types';

export type PolicyScopeType = 'global' | 'company' | 'branch' | 'warehouse' | 'project' | 'own' | 'team' | 'custom';
export type PolicyEffect = 'allow' | 'deny';

export interface PermissionPolicy {
  id: string;
  companyId: string | null;
  roleId: string | null;
  permissionKey: string;
  scopeType: PolicyScopeType;
  scopeValues: Record<string, unknown>;
  conditions: Record<string, unknown>;
  effect: PolicyEffect;
  priority: number;
  isActive: boolean;
  createdAt: string | null;
}

export interface PermissionPolicyForm {
  id?: string;
  roleId: string;
  permissionKey: string;
  scopeType: PolicyScopeType;
  scopeValueText: string;
  effect: PolicyEffect;
  priority: number;
  isActive: boolean;
  ownOnly: boolean;
  denySelfApproval: boolean;
  maxAmount: string;
}

type Row = Record<string, any>;

function mapPolicy(row: Row): PermissionPolicy {
  return {
    id: String(row.id),
    companyId: row.company_id || null,
    roleId: row.role_id || null,
    permissionKey: String(row.permission_key || ''),
    scopeType: (row.scope_type || 'company') as PolicyScopeType,
    scopeValues: (row.scope_values || {}) as Record<string, unknown>,
    conditions: (row.conditions || {}) as Record<string, unknown>,
    effect: (row.effect || 'allow') as PolicyEffect,
    priority: Number(row.priority || 100),
    isActive: Boolean(row.is_active),
    createdAt: row.created_at || null,
  };
}

function parseScopeValues(scopeType: PolicyScopeType, text: string) {
  const values = text.split(',').map((item) => item.trim()).filter(Boolean);
  if (scopeType === 'warehouse') return { warehouse_ids: values };
  if (scopeType === 'project') return { project_ids: values };
  if (scopeType === 'branch') return { branch_ids: values };
  if (scopeType === 'team') return { team_ids: values };
  return values.length ? { values } : {};
}

function buildConditions(form: PermissionPolicyForm) {
  const conditions: Record<string, unknown> = {};
  if (form.ownOnly) conditions.own_only = true;
  if (form.denySelfApproval) conditions.deny_self_approval = true;
  if (form.maxAmount.trim()) conditions.max_amount = Number(form.maxAmount);
  return conditions;
}

export const permissionPolicyService = {
  async list(companyId: string): Promise<PermissionPolicy[]> {
    await enforceSettingsPermission('settings.permissions.view', { companyId });
    let query = (supabase as any)
      .from('permission_policies')
      .select('*')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: false });

    if (companyId) query = query.or(`company_id.eq.${companyId},company_id.is.null`);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapPolicy);
  },

  async save(form: PermissionPolicyForm, companyId: string, actorUserId?: string | null) {
    await enforceSettingsPermission('settings.permissions.manage', { companyId });
    const payload = {
      company_id: companyId || null,
      role_id: form.roleId || null,
      permission_key: form.permissionKey,
      scope_type: form.scopeType,
      scope_values: parseScopeValues(form.scopeType, form.scopeValueText),
      conditions: buildConditions(form),
      effect: form.effect,
      priority: form.priority,
      is_active: form.isActive,
      created_by: actorUserId || null,
    };

    const query = form.id
      ? (supabase as any).from('permission_policies').update(payload).eq('id', form.id)
      : (supabase as any).from('permission_policies').insert(payload);

    const { error } = await query;
    if (error) throw error;

    await (supabase as any).from('audit_logs').insert({
      actor_user_id: actorUserId || null,
      action_code: form.id ? 'POLICY_UPDATE' : 'POLICY_CREATE',
      module_code: 'permissions',
      entity_type: 'permission_policy',
      entity_id: form.id || null,
      summary: `${form.id ? 'Cập nhật' : 'Tạo'} policy ${form.permissionKey}`,
      after_data: payload,
    });
  },

  async remove(policy: PermissionPolicy, actorUserId?: string | null) {
    await enforceSettingsPermission('settings.permissions.manage', { companyId: policy.companyId || undefined });
    const { error } = await (supabase as any)
      .from('permission_policies')
      .delete()
      .eq('id', policy.id);
    if (error) throw error;

    await (supabase as any).from('audit_logs').insert({
      actor_user_id: actorUserId || null,
      action_code: 'POLICY_DELETE',
      module_code: 'permissions',
      entity_type: 'permission_policy',
      entity_id: policy.id,
      summary: `Xóa policy ${policy.permissionKey}`,
      before_data: policy,
    });
  },

  buildInitialForm(roles: PermissionRole[], permissionKey = ''): PermissionPolicyForm {
    return {
      roleId: roles[0]?.id || '',
      permissionKey,
      scopeType: 'company',
      scopeValueText: '',
      effect: 'allow',
      priority: 100,
      isActive: true,
      ownOnly: false,
      denySelfApproval: false,
      maxAmount: '',
    };
  },
};
